import React, { ReactNode, useMemo } from 'react';

import { FormComponent, UiComponent } from 'components';
import { tableWrap, tableToolbar } from 'styles/table';
import { IContent, IPageOption } from 'types';
import { BsPlusLg } from 'react-icons/bs';
import useModal from 'hooks/useModal';
import ContentsForm from '../Form';
import dayjs from 'dayjs';
import ThumbnailPreview from '../ThumbnailPreview';
import { getTypeText } from '../utils';
import { useNavigate } from 'react-router-dom';
import { PLATFORM_MENU_URL } from 'data/menu';
import { BiPencil } from 'react-icons/bi';

const COLS: string[] = [
  'ID',
  '대표이미지',
  '콘텐츠명',
  '서브콘텐츠명',
  '유형',
  '등록일',
];

interface Props {
  pageOption: IPageOption;
  handlePage: (option: IPageOption) => void;
  data: IContent[];
  totalPage: number;
  toolbar?: ReactNode;
}

function List({ data, toolbar, ...pageOptions }: Props) {
  const navigate = useNavigate();
  const { handleModal } = useModal();

  const handleContentsModal = (content: IContent | null) => {
    handleModal('', <ContentsForm content={content} />, <></>);
  };

  const handleThumbnailClick = (thumbnailUrl: string) => {
    handleModal('', <ThumbnailPreview src={thumbnailUrl} />, <></>);
  };

  const renderer: Record<
    (typeof COLS)[number],
    (data: IContent & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      대표이미지: ({ thumbnailUrl }: IContent) => (
        <img
          src={thumbnailUrl}
          alt="thumbnail"
          css={{ width: '32px', cursor: 'pointer' }}
          onClick={() => handleThumbnailClick(thumbnailUrl)}
        />
      ),
      ID: ({ id }: IContent) => <>{id}</>,
      콘텐츠명: (content: IContent) => (
        <p
          css={{
            textAlign: 'left',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          title="콘텐츠 메타 정보 수정"
          onClick={() => handleContentsModal(content)}
        >
          {content.title}
          <span css={{ marginLeft: '4px' }}>
            <BiPencil size={11} />
          </span>
        </p>
      ),
      서브콘텐츠명: ({ subTitle }: IContent) => (
        <p css={{ textAlign: 'left' }}>{subTitle || '-'}</p>
      ),
      유형: ({ type, id }: IContent) => (
        <>
          {type === 'INTERNAL' ? (
            <p
              css={{ textDecoration: 'underline', cursor: 'pointer' }}
              title="상세페이지 에디터 이동"
              onClick={() => navigate(`${PLATFORM_MENU_URL.콘텐츠}/${id}`)}
            >
              {getTypeText(type)}
              <span css={{ marginLeft: '4px' }}>
                <BiPencil size={11} />
              </span>
            </p>
          ) : (
            <p>{getTypeText(type)}</p>
          )}
        </>
      ),
      등록일: ({ createdAt }: IContent) => (
        <>{dayjs(createdAt).format('YYYY-MM-DD HH:mm')}</>
      ),
    }),
    [],
  );

  return (
    <>
      <UiComponent.Box>
        <div css={tableToolbar}>
          {toolbar ?? <div />}
          <div>
            <UiComponent.Button
              variant="contained"
              color="primary"
              onClick={() => handleContentsModal(null)}
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '0 4px',
              }}
            >
              <BsPlusLg size={15} />
              <p css={{ fontSize: '14px' }}>콘텐츠 추가</p>
            </UiComponent.Button>
            <FormComponent.Select
              css={{ width: '110px' }}
              options={[{ name: '등록일', value: '1' }]}
            />
            <FormComponent.Select
              css={{ width: '90px' }}
              options={[
                { name: '20', value: '20' },
                { name: '50', value: '50' },
                { name: '100', value: '100' },
              ]}
            />
          </div>
        </div>
        <div css={tableWrap}>
          <UiComponent.Table
            cols={COLS}
            renderer={renderer}
            data={data}
            sticky
          />
        </div>
        <UiComponent.Pagination {...pageOptions} />
      </UiComponent.Box>
    </>
  );
}

export default List;
